import { useState, useEffect, useRef, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { readsFrom } from '../utils/migrationConfig';
import { apiVerifyEmailCode } from '../api/users';
import { useTranslation } from 'react-i18next';
import { useThemeColors } from '../context/ThemeContext';
import { dsv2, dsv2Light } from '../styles/designSystemV2';
import { X, Mail, ShieldCheck } from 'lucide-react';

// Modal reutilizable de "código por email" (verificación de correo + 2FA).
// Envía un código al abrir (una sola vez), lo pide, y verifica vía callable.
export default function EmailCodeModal({ purpose, title, description, deviceId, onSuccess, onClose }) {
  const { t } = useTranslation();
  const { isDark } = useThemeColors();
  const tk = isDark ? dsv2 : dsv2Light;

  const [phase, setPhase] = useState('sending'); // sending | enter | verifying
  const [sentTo, setSentTo] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const sentOnce = useRef(false);

  const send = useCallback(async () => {
    setError('');
    setPhase('sending');
    try {
      const res = await httpsCallable(functions, 'sendEmailCode')({ purpose });
      setSentTo(res.data?.sentTo || '');
      setPhase('enter');
      setCooldown(60);
    } catch (e) {
      setPhase('enter');
      setError(e?.message || t('verify.errGeneric'));
    }
  }, [purpose, t]);

  useEffect(() => {
    if (sentOnce.current) return;
    sentOnce.current = true;
    send();
  }, [send]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  // 🔴 EL MOTIVO DEL FALLO VIENE EN PALABRA CLAVE, NO EN FRASE.
  //
  // La callable devuelve sus mensajes EN ESPAÑOL desde dentro del servidor, así
  // que a un cliente con la app en inglés le salían sin traducir. El API devuelve
  // `reason` y el texto se elige aquí, por i18n. Lo que no esté en la tabla cae al
  // mensaje genérico: inventar un texto para un motivo desconocido es peor que
  // decir «no se pudo».
  const TEXTO_DEL_FALLO = {
    no_pending: 'verify.errNoPending',
    wrong_purpose: 'verify.errNoPending',
    expired: 'verify.errExpired',
    too_many_attempts: 'verify.errTooMany',
    wrong_code: 'verify.errWrongCode',
    invalid_format: 'verify.errFormat',
    invalid_purpose: 'verify.errGeneric',
  };

  const verify = async () => {
    if (!/^\d{6}$/.test(code)) { setError(t('verify.errFormat')); return; }
    setError('');
    setPhase('verifying');
    try {
      // Patrón dual. Con `users` en Postgres la callable se NIEGA a correr (su
      // éxito sería escribir en la base que ya nadie lee), así que hasta ahora se
      // gastaba un código que no se podía canjear: llegaba el correo, el cliente
      // lo tecleaba bien y recibía un error técnico sin traducir.
      if (await readsFrom('users') === 'postgres') {
        await apiVerifyEmailCode({ purpose, code, deviceId: deviceId || null });
      } else {
        await httpsCallable(functions, 'verifyEmailCode')({ purpose, code, deviceId: deviceId || null });
      }
      onSuccess?.();
    } catch (e) {
      setPhase('enter');
      const clave = TEXTO_DEL_FALLO[e?.reason];
      setError(clave ? t(clave) : (e?.message || t('verify.errGeneric')));
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 12000, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 400, background: tk.surface, borderRadius: 20,
          border: `1px solid ${tk.outline}`, padding: 24, position: 'relative',
        }}
      >
        {onClose && (
          <button onClick={onClose} style={{ position: 'absolute', top: 14, right: 14, background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={20} color={tk.textMuted} />
          </button>
        )}

        <div style={{
          width: 52, height: 52, borderRadius: 15, margin: '0 auto 14px',
          background: tk.redSoft, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {purpose === 'login_2fa' ? <ShieldCheck size={26} color={tk.red} /> : <Mail size={26} color={tk.red} />}
        </div>

        <h2 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: tk.text, textAlign: 'center' }}>{title}</h2>
        <p style={{ margin: '0 0 18px', fontSize: 13.5, color: tk.textMuted, textAlign: 'center', lineHeight: 1.55 }}>
          {phase === 'sending'
            ? t('verify.sending')
            : (sentTo ? t('verify.sentTo', { email: sentTo }) : description)}
        </p>

        <input
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={e => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
          onKeyDown={e => { if (e.key === 'Enter') verify(); }}
          placeholder="••••••"
          disabled={phase === 'sending' || phase === 'verifying'}
          style={{
            width: '100%', textAlign: 'center', fontSize: 30, fontWeight: 800,
            letterSpacing: '0.4em', padding: '12px 0', borderRadius: 12,
            border: `1.5px solid ${error ? tk.red : tk.outline}`, background: tk.bg,
            color: tk.text, outline: 'none', boxSizing: 'border-box',
          }}
        />

        {error && <p style={{ margin: '10px 0 0', fontSize: 12.5, color: tk.red, textAlign: 'center' }}>{error}</p>}

        <button
          onClick={verify}
          disabled={phase !== 'enter' || code.length !== 6}
          style={{
            width: '100%', marginTop: 16, padding: '13px 0', borderRadius: 12, border: 'none',
            background: (phase === 'enter' && code.length === 6) ? tk.red : tk.outline,
            color: '#fff', fontSize: 15, fontWeight: 700, cursor: (phase === 'enter' && code.length === 6) ? 'pointer' : 'default',
          }}
        >
          {phase === 'verifying' ? t('verify.verifying') : t('verify.verifyBtn')}
        </button>

        <button
          onClick={send}
          disabled={cooldown > 0 || phase === 'sending' || phase === 'verifying'}
          style={{
            width: '100%', marginTop: 10, padding: 8, background: 'none', border: 'none',
            color: cooldown > 0 ? tk.textMuted : tk.red, fontSize: 13, fontWeight: 600,
            cursor: cooldown > 0 ? 'default' : 'pointer',
          }}
        >
          {cooldown > 0 ? t('verify.resendIn', { s: cooldown }) : t('verify.resend')}
        </button>
      </div>
    </div>
  );
}
